import { inject } from "@angular/core";
import { ActivatedRouteSnapshot, ResolveFn, Route, Router } from "@angular/router";

import { firstValueFrom } from "rxjs";

import { authGuard } from "@core/guards/auth.guard";
import { DocumentHttpService } from "@documentation/services/document-http.service";
import { DocumentsStore } from "@documentation/state/documents.store";

import { unsavedChangesGuard } from "../guards/unsaved-changes.guard";
import { DocumentationRoutesConstants } from "./documentation.routes.constants";

const documentDetailResolver: ResolveFn<void> = (route: ActivatedRouteSnapshot) => {
  const store = inject(DocumentsStore);
  const id = route.paramMap.get("id");

  if (id) {
    store.loadDocument(id);
  }
};

// Resolver to redirect to first document when no ID is provided
const documentListResolver: ResolveFn<void> = async (route: ActivatedRouteSnapshot) => {
  const router = inject(Router);
  const documentHttpService = inject(DocumentHttpService);
  const store = inject(DocumentsStore);

  // Load documents and redirect to first one
  try {
    const rootFolder = await firstValueFrom(documentHttpService.getDocumentListItems());
    const firstDocument = rootFolder.content.find((item) => item.type === "document");
    if (firstDocument?.id) {
      store.setNavigatingSafely(true);
      router.navigate([DocumentationRoutesConstants.document(firstDocument.id)]);
    }
  } catch (error) {
    console.error("Failed to load documents for redirect", error);
  }
};

export const documentationRoutes: Route = {
  path: DocumentationRoutesConstants.MAIN,
  canActivate: [authGuard],
  loadComponent: () => import("@documentation/documentation.component").then((m) => m.DocumentationComponent),
  children: [
    {
      path: "roles/import/:id",
      loadComponent: () => import("@documentation/components/role-import/role-import.component").then((m) => m.RoleImportComponent),
      title: "Import Role"
    },
    {
      path: "",
      redirectTo: DocumentationRoutesConstants.DOCUMENT,
      pathMatch: "full"
    },
    {
      path: DocumentationRoutesConstants.DOCUMENT,
      loadComponent: () => import("@documentation/components/document.component").then((m) => m.DocumentComponent),
      resolve: {
        redirect: documentListResolver
      },
      title: "Document"
    },
    {
      path: `${DocumentationRoutesConstants.DOCUMENT}/:id`,
      loadComponent: () => import("@documentation/components/document.component").then((m) => m.DocumentComponent),
      canDeactivate: [unsavedChangesGuard],
      resolve: {
        document: documentDetailResolver
      },
      title: "Document"
    }
  ]
};
